import {
  PageableListDtoOfPatientDuplicatePairDto,
  QueryFactory,
} from '@lmt/shared-types';
import { PatientDuplicatePairDto } from '@lmt/shared-types';
import { toSortingQueryParams } from '@lmt/unicorn-shared/app/components/common/table/operations';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { VariablesActions } from '@lmt/unicorn-shared/app/features/variables/reducer';
import { getLastPatientDuplicateDateSelector } from '@lmt/unicorn-shared/app/features/variables/selectors';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './PingMergeAssistant.module.scss';

/*
Pings backend every 5 minute to refresh the last patient duplicate creation date.
 */
export const PingMergeAssistant = () => {
  const i18next = useScopedTranslation(
    'merge_assistant.new_duplicates_confirmation',
  );
  const tabs = useTabs();
  const dispatch = useUniDispatch();
  const modals = useModal();

  const lastPatientDuplicateDate = useUniSelector(
    getLastPatientDuplicateDateSelector,
  );

  const CustomToastWithLink = (duplicatesCount: number) => (
    <div className={styles.container}>
      <div className={styles.header}>
        {i18next.t('title', { count: duplicatesCount })}
      </div>
      <div className={styles.text}>{i18next.t('text')}</div>
      <div>
        <UniLink
          color={ButtonColor.Primary}
          title={i18next.t('link_text')}
          onClick={() => tabs.openTab(pages.mergeAssistant.link())}
        />
      </div>
    </div>
  );

  const changeLastDuplicateDate = useCallback(
    (date: Date) => {
      dispatch(
        VariablesActions.lastPatientDuplicateDateChanged(date.getTime()),
      );
    },
    [dispatch],
  );

  const duplicatesQuery = QueryFactory.PatientDuplicateQuery.useGetQuery(
    {
      showDeleted: false,
      ...toSortingQueryParams({
        sortBy: nameof<PatientDuplicatePairDto>('createdAt'),
        desc: true,
      }),
    },
    {
      throwOnError: false,
      refetchInterval: 1 * 60 * 1000,
      refetchIntervalInBackground: false,
    },
  );

  useEffect(() => {
    const data = duplicatesQuery.data;
    if (!data) return;
    if (data.data.length > 0) {
      if (
        !lastPatientDuplicateDate ||
        data.data[0].createdAt.getTime() > lastPatientDuplicateDate
      ) {
        toast.dismiss();
        toast.warn(CustomToastWithLink(data.data.length), {
          closeOnClick: true,
          autoClose: false,
        });
        changeLastDuplicateDate(data.data[0].createdAt);
      }
    }
  }, [duplicatesQuery.data]);

  return null;
};
